/**
 * Drag and Drop Logic Hook
 *
 * Handles column reordering via drag and drop using @dnd-kit.
 */
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import type { AppTable } from "../features";
import type { RowData } from "@tanstack/react-table";

export const useDragAndDropLogic = <T extends RowData>(table: AppTable<T>) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id === undefined || over?.id === undefined) return;

    const fullOrder = table.getAllLeafColumns().map((col) => col.id);
    const oldIndex = fullOrder.indexOf(active.id as string);
    let newIndex = fullOrder.indexOf(over.id as string);

    if (oldIndex === -1 || newIndex === -1) return;

    const isLocked = (id: string) => id === "actions" || id === "details" || id === "select";
    if (isLocked(fullOrder[newIndex])) {
      if (fullOrder[newIndex] === "select") newIndex = 1;
      else if (fullOrder[newIndex] === "actions" || fullOrder[newIndex] === "details") newIndex = Math.max(0, fullOrder.length - 2);
    }

    newIndex = Math.max(0, Math.min(fullOrder.length - 1, newIndex));
    if (newIndex === oldIndex) return;

    const newOrder = arrayMove(fullOrder, oldIndex, newIndex);
    table.setColumnOrder(newOrder);
  };

  const columnIds = table.getVisibleLeafColumns().map((col) => col.id);

  return {
    sensors,
    handleDragEnd,
    columnIds,
  };
};
